import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Calendar, Check, X, ArrowRight, AlertCircle, FileText, FolderOpen } from 'lucide-react';
import { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import axios from 'axios';
import { toast } from 'sonner';
import DatePickerWithInput from './DatePickerWithInput';
import { capitalizeFirst } from './EntriesList';

registerLocale('el', el);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PostponementWizard = ({
  isOpen,
  onClose,
  entry,
  entries,
  cases,
  onRefresh
}) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [conflictingEntries, setConflictingEntries] = useState([]);
  const [isNewCase, setIsNewCase] = useState(null);
  const [, setSelectedCaseId] = useState(entry?.case_id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset wizard when opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDate(null);
      setConflictingEntries([]);
      setIsNewCase(null);
      setSelectedCaseId(entry?.case_id || '');
    }
  }, [isOpen, entry]);

  // Find conflicting entries when date is selected
  useEffect(() => {
    if (selectedDate && entries) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const conflicts = entries.filter(e => {
        const entryDate = e.date ? e.date.split('T')[0] : '';
        return entryDate === dateStr && e.id !== entry?.id;
      });
      setConflictingEntries(conflicts);
    }
  }, [selectedDate, entries, entry]);

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep(2);
  };

  const handleAcceptDate = () => {
    setStep(3);
  };

  const handleRejectDate = () => {
    setStep(1);
    setSelectedDate(null);
  };

  const handleCaseChoice = (isNew) => {
    setIsNewCase(isNew);
    if (!isNew) {
      setSelectedCaseId(entry?.case_id || '');
    }
    setStep(4);
  };

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error('Παρακαλώ επιλέξτε ημερομηνία');
      return;
    }

    setIsSubmitting(true);
    // Format date correctly without timezone issues
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    try {
      const response = await axios.post(`${API}/entries/${entry.id}/postpone`, {
        new_date: formattedDate,
        recalculate_deadlines: true
      });

      toast.success(response.data.message || 'Η αναβολή καταχωρήθηκε επιτυχώς');
      if (onRefresh) await onRefresh();
      onClose();
    } catch (error) {
      console.error('Error handling postponement:', error);
      toast.error('Σφάλμα κατά την καταχώρηση αναβολής');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
          <Calendar className="w-6 h-6 text-red-700" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-900">Βήμα 1: Ημερομηνία αναβολής</h3>
        <p className="text-sm text-slate-500 mt-1">Επιλέξτε την προτεινόμενη ημερομηνία αναβολής</p>
      </div>

      <div className="flex justify-center">
        <DatePickerWithInput
          selected={selectedDate}
          onChange={handleDateSelect}
          minDate={new Date()}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-3">
          <AlertCircle className="w-6 h-6 text-amber-600" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-900">Βήμα 2: Έλεγχος εκκρεμοτήτων</h3>
        <p className="text-sm text-slate-500 mt-1">
          Επιλεγμένη ημερομηνία: <strong className="text-slate-900">{formatDate(selectedDate)}</strong>
        </p>
      </div>

      {conflictingEntries.length > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <p className="text-sm font-medium text-amber-800 mb-3">
            Υπάρχουν {conflictingEntries.length} εκκρεμότητες στην ίδια ημερομηνία:
          </p>
          <ul className="space-y-2 max-h-40 overflow-y-auto">
            {conflictingEntries.map((e, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-amber-700 bg-white rounded-xl px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                <FileText className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium">{capitalizeFirst(e.title)}</span>
                <span className="text-amber-500">•</span>
                <span>{(e.client_name || 'Χωρίς πελάτη').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <Check className="w-4 h-4" />
            Δεν υπάρχουν άλλες εκκρεμότητες στην επιλεγμένη ημερομηνία
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3 mt-6">
        <p className="text-sm font-medium text-slate-700 text-center">Αποδέχεστε την ημερομηνία;</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={handleAcceptDate}
            className="flex items-center gap-2 px-6 py-2.5 bg-red-700 text-white rounded-xl hover:bg-red-800 transition-colors font-medium shadow-sm"
            data-testid="postponement-accept-date-btn"
          >
            <Check className="w-4 h-4" />
            Αποδεκτή
          </button>
          <button
            onClick={handleRejectDate}
            className="flex items-center gap-2 px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-medium"
            data-testid="postponement-reject-date-btn"
          >
            <X className="w-4 h-4" />
            Άλλη ημερομηνία
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
          <FolderOpen className="w-6 h-6 text-red-700" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-900">Βήμα 3: Τύπος υπόθεσης</h3>
        <p className="text-sm text-slate-500 mt-1">Η αναβολή αφορά νέα υπόθεση;</p>
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={() => handleCaseChoice(false)}
          className="flex items-center gap-3 p-4 border border-slate-200 rounded-2xl hover:border-red-700 hover:bg-red-50 transition-all text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
          data-testid="postponement-existing-case-btn"
        >
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-red-700" />
          </div>
          <div>
            <p className="font-medium text-slate-900">Καταχωρημένη υπόθεση</p>
            <p className="text-sm text-slate-500">Εγγραφή στην υπάρχουσα υπόθεση</p>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
        </button>

        <button
          onClick={() => handleCaseChoice(true)}
          className="flex items-center gap-3 p-4 border border-slate-200 rounded-2xl hover:border-red-700 hover:bg-red-50 transition-all text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
          data-testid="postponement-new-case-btn"
        >
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <FolderOpen className="w-5 h-5 text-red-700" />
          </div>
          <div>
            <p className="font-medium text-slate-900">Νέα υπόθεση</p>
            <p className="text-sm text-slate-500">Δημιουργία νέας υπόθεσης για την αναβολή</p>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
          <Check className="w-6 h-6 text-red-700" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-900">Βήμα 4: Επιβεβαίωση</h3>
        <p className="text-sm text-slate-500 mt-1">Ελέγξτε τα στοιχεία της αναβολής</p>
      </div>

      <div className="bg-slate-50 rounded-2xl p-4 space-y-3 border border-slate-200">
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Αρχική ημερομηνία:</span>
          <span className="text-sm font-medium text-slate-900">{formatDate(entry?.date)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Νέα ημερομηνία:</span>
          <span className="text-sm font-semibold text-red-700">{formatDate(selectedDate)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Τύπος:</span>
          <span className="text-sm font-medium text-slate-900">
            {isNewCase ? 'Νέα υπόθεση' : 'Καταχωρημένη υπόθεση'}
          </span>
        </div>
      </div>

      <div className="flex gap-3 justify-center mt-6">
        <button
          onClick={() => setStep(3)}
          className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors font-medium"
          data-testid="postponement-back-btn"
        >
          Πίσω
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex items-center gap-2 px-6 py-2.5 bg-red-700 text-white rounded-xl hover:bg-red-800 transition-colors font-medium disabled:opacity-50 shadow-sm"
          data-testid="postponement-submit-btn"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin">⏳</span>
              Καταχώρηση...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Καταχώρηση
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4].map((s) => (
        <div key={s} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
            step >= s
              ? 'bg-red-700 text-white'
              : 'bg-slate-100 text-slate-500'
          }`}>
            {s}
          </div>
          {s < 4 && (
            <div className={`w-8 h-1 transition-colors ${step > s ? 'bg-red-700' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Calendar className="w-5 h-5 text-red-700" />
            Αναβολή δικασίμου
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {renderStepIndicator()}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostponementWizard;
